import { Controller } from "@hotwired/stimulus";

// Renders a Cloudflare Turnstile widget with explicit rendering so it
// survives Turbo re-renders (api.js only scans for implicit widgets once, on
// script load). The widget injects a hidden `cf-turnstile-response` input
// into the surrounding form.
//
// Turnstile's lifecycle is re-emitted as DOM events — `turnstile:solved`,
// `turnstile:error`, `turnstile:expired`, `turnstile:timeout` and
// `turnstile:loadfailed` — so a surrounding controller can gate submission on
// them with `data-action`. A form that only needs the token can ignore all of
// it and let the hidden input do the work, which is what signup does.
export default class extends Controller {
  static values = { sitekey: String };

  connect() {
    this.active = true;

    loadTurnstile()
      .then(() => {
        // Turbo may have swapped the page out while the script was loading;
        // rendering into a detached element would leak an orphaned widget.
        if (!this.active) return;

        this.widgetId = window.turnstile.render(this.element, {
          sitekey: this.sitekeyValue,
          callback: (token) => this.dispatch("solved", { detail: { token } }),
          "error-callback": (code) =>
            this.dispatch("error", { detail: { code } }),
          "expired-callback": () => this.dispatch("expired"),
          // Fires when an interactive challenge goes unsolved for too long.
          // Turnstile resets the widget on its way here, so a visitor who sees
          // this has something to act on.
          "timeout-callback": () => this.dispatch("timeout"),
        });
      })
      .catch((error) => {
        // Blocked, offline, or render() rejected its options. No callback will
        // ever fire, so this is the only signal that nothing is coming —
        // listeners that gate a submit button need it to avoid leaving the
        // button disabled with no explanation.
        console.error(error);
        if (this.active) this.dispatch("loadfailed");
      });
  }

  disconnect() {
    this.active = false;

    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
      this.widgetId = null;
    }
  }
}

let turnstileScriptPromise;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();

  turnstileScriptPromise ||= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => {
      // Don't cache the failure: let the next connect() retry the load.
      script.remove();
      turnstileScriptPromise = undefined;
      reject(new Error("Failed to load Turnstile"));
    };
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}
